import React, { useContext, useCallback } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { Tooltip } from '@jutro/components';
import appConfig from 'app-config';
import { MLTooltipComponent } from 'ml-components-platform-react';
import CoveragesTableComponent from '../CoveragesTableComponent/CoveragesTableComponent';
import metadata from './PolicySummaryVehicleInfoComponent.metadata.json5';
import messages from './PolicySummaryVehicleInfoComponent.messages';
import styles from './PolicySummaryVehicleInfoComponent.module.scss';

import { IntlContext } from '@jutro/legacy/locale';

const termTypeLabels = {
    Annual: messages.summaryAnnualTerm,
    HalfYear: messages.summaryHalfYearTerm
};

function SummaryTooltipComponent(props) {
    const translator = useTranslator();

    SummaryTooltipComponent.propTypes = {
        tooltipContent: PropTypes.shape({}).isRequired
    };
    const { tooltipContent } = props;

    return (
        <Tooltip
            id="summaryTooltip"
            placement="right"
            trigger="click"
            content={translator(tooltipContent)}
        >
            <span className={styles.tooltipOverride}>
                <i className="mir mi-help-outline" />
            </span>
        </Tooltip>
    );
}

function PolicySummaryVehicleInfoComponent(props) {
    const {
        model: vehicle,
        index: vehicleIndex,
        isNamedNonOwner,
        coverages,
        termType,
        editCoverages,
        gpaMode,
        isPolicyChange,
        displayedLineCoverages,
        isModernProduct
    } = props;
    const isQnB = appConfig.portalCode === 'QB';
    const translator = useTranslator();
    const intl = useContext(IntlContext);


    const covAmountDisplayed = useCallback((coverage) => {
        if (displayedLineCoverages.has(coverage.code_ML)) {
            const vehId = displayedLineCoverages.get(coverage.code_ML);
            return vehId !== vehicle.publicID.value;
        }
        return false;
    }, [displayedLineCoverages, vehicle.publicID]);

    const getVehiclePremium = useCallback((coverage) => {
        let totalLineCovPremium;
        let tempValue;
        let returnObj = {};
        let totalVehMonthlyPremium;
        coverage.forEach((cov) => {
            if (cov.isLineCovCost_Ext === true) {
                tempValue = cov.lineCovCost_Ext.amount;
                if (!covAmountDisplayed(cov)) {
                    displayedLineCoverages.set(cov.code_ML, vehicle.publicID.value);
                    totalLineCovPremium = totalLineCovPremium === undefined ? 0 : totalLineCovPremium;
                    totalLineCovPremium += tempValue;
                    totalVehMonthlyPremium = totalLineCovPremium + vehicle.vehicleCost_ML.amount.value;
                    returnObj = {
                        amount: totalVehMonthlyPremium,
                        currency: 'usd'
                    };
                } else {
                    returnObj = vehicle.vehicleCost_ML;
                }
            } else {
                returnObj = vehicle.vehicleCost_ML;
            }
        });
        return returnObj;
    }, [covAmountDisplayed, displayedLineCoverages, vehicle.publicID, vehicle.vehicleCost_ML]);

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const formattedVehiclePremium = useCallback(() => {
        const vehiclePremium = getVehiclePremium(coverages.lineCoverages);
        const vehiclePremiumAmount = _.get(vehiclePremium, 'amount', undefined);
        return (
            <>
                <strong>{_.isUndefined(vehiclePremiumAmount) ? '' : formatAmountString(vehiclePremium.value)}</strong>
                {translator(termTypeLabels[termType.code])}
            </>
        );
    }, [coverages.lineCoverages, formatAmountString, getVehiclePremium, termType.code, termTypeLabels, translator]);

    const generateColumnOverrides = useCallback((columnData) => {
        const overrideProps = columnData.map((coverage, index) => ({
            [`coverageCell${index}`]: {
                visible: coverage.selected
            }
        }));
        return Object.assign({}, ...overrideProps);
    }, []);


    const getNonOwnerLabel = () => {
      return isModernProduct
        ? translator(messages.nonOwnerHeadingNP)
        : translator(messages.nonOwnerHeading);
    };
    
    const getNonOwnerTooltipContent = () => {
      return isModernProduct
        ? translator(messages.summaryNamedNonOwnerTooltipContentNP)
        : translator(messages.summaryNamedNonOwnerTooltipContent);
    };

    const namedNonOwnerML = _.get(vehicle, 'namedNonOwner_ML.value', undefined);
    const overrideProps = {
        vehicleIcon: {
            className: gpaMode ? "icon-icon-car-white" : "icon-ml-car-icon-outline"
        },
        vehicleCount: {
            visible: _.isUndefined(namedNonOwnerML) || namedNonOwnerML === false && !gpaMode
        },
        vehiclex: {
            visible : !gpaMode,
            content: translator(messages.summaryVehicleX, { vehicleIndex: (vehicleIndex + 1) })
        },
        vehicleModel: {
            visible: _.isUndefined(namedNonOwnerML) || namedNonOwnerML === false
        },
        yearMakeModel: {
            content: `${vehicle.year.value} ${vehicle.make.value} ${vehicle.model.value} `
        },
        trim: {
            content: (vehicle.trim_ML.value) ? `${vehicle.trim_ML.value} ` : ' '
        },
        editCoverages: {
            visible: (_.isUndefined(namedNonOwnerML) || namedNonOwnerML === false) && !gpaMode && isPolicyChange && !isQnB,
            onClick: editCoverages           
        }, 
        namedNonOwner:{
            content:getNonOwnerLabel()
        },
        namedNonOwnerTooltip:{
            tooltipContent:getNonOwnerTooltipContent()
        },
        namedNonOwnerLabel: {
            visible: isNamedNonOwner === true,
        },
        coveragesTable: {
            model: vehicle,
            displayedLineCoverages,
            isModernProduct
        },
        vehicleTotalLabel: {
            content: isNamedNonOwner ? translator(messages.coverageTotal) : translator(messages.vehicleTotal)
        },
        premiumTerm: {
            content: formattedVehiclePremium()
        },
        vinX : {
            content : translator(messages.summaryVinX,{number : vehicle.vin.value}),
            visible : gpaMode
        },
        ...generateColumnOverrides(_.get(vehicle, 'coverages', []))
    };

    const resolvers = {
        resolveComponentMap: {
            coveragesTableComponent: CoveragesTableComponent,
            summaryTooltipComponent: SummaryTooltipComponent,
            vehicleTooltip: MLTooltipComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={vehicle}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={styles}
        />
    );
}

PolicySummaryVehicleInfoComponent.propTypes = {
    model: PropTypes.shape({}).isRequired,
    coverages: PropTypes.shape({
        lineCoverages: PropTypes.shape([])
    }).isRequired,
    isNamedNonOwner: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
    termType: PropTypes.string.isRequired,
    editCoverages: PropTypes.func.isRequired,
    gpaMode:PropTypes.bool,
    isPolicyChange: PropTypes.bool
};
PolicySummaryVehicleInfoComponent.defaultProps ={
    gpaMode: false,
    isPolicyChange: true
}

export default PolicySummaryVehicleInfoComponent;